import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { iconSize } from '@munifocus/tokens';
import { Button } from '../primitives';

/**
 * How loud the action is, not how it is drawn.
 *
 * Pages used to pick `variant` themselves, and picked differently: the same
 * "Add" button was `contained` on one registry and `outlined` on the next, and
 * an export sat at the same weight as the create beside it. Naming the intent
 * instead of the paint is what keeps that from drifting again — there is one
 * place that decides what "primary" looks like.
 */
export type ActionTone = 'primary' | 'secondary' | 'quiet' | 'danger';

const TONE: Record<ActionTone, { variant: 'contained' | 'outlined' | 'text'; color?: 'error' }> = {
  primary: { variant: 'contained' },
  secondary: { variant: 'outlined' },
  quiet: { variant: 'text' },
  danger: { variant: 'outlined', color: 'error' },
};

/** Every action icon in a toolbar or page header is this step of the scale. */
export const ACTION_ICON_SIZE = iconSize.md;

export interface ActionButtonProps {
  label: string;
  /** The `Icons.*` component, not an element — the size is not the caller's to pick. */
  icon?: LucideIcon;
  onClick?: () => void;
  tone?: ActionTone;
  disabled?: boolean;
  /** Renders as a submit control inside a form rather than a click handler. */
  type?: 'button' | 'submit';
  href?: string;
  /** Escape hatch for the rare action that needs a badge or a count beside it. */
  endAdornment?: ReactNode;
  'aria-label'?: string;
}

/**
 * The one button for a table toolbar or a page header.
 *
 * **Why this exists.** Across thirty-one list screens the same "add a record"
 * button was written thirty-one times, and it showed: icon sizes of 14, 15 and
 * 16 in adjacent toolbars, `size="small"` on some and the default on others,
 * and three different variants for the same job. None of it was a decision —
 * it was thirty-one people-shaped copies of one control. A table that looks
 * different from the table next to it reads as a different application.
 */
export function ActionButton({
  label,
  icon: Icon,
  onClick,
  tone = 'primary',
  disabled,
  type = 'button',
  href,
  endAdornment,
  'aria-label': ariaLabel,
}: ActionButtonProps) {
  const { variant, color } = TONE[tone];
  return (
    <Button
      size="small"
      type={type}
      variant={variant}
      color={color}
      href={href}
      disabled={disabled}
      onClick={onClick}
      aria-label={ariaLabel}
      startIcon={Icon ? <Icon size={ACTION_ICON_SIZE} /> : undefined}
      endIcon={endAdornment}
      sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
    >
      {label}
    </Button>
  );
}
